import { Plus, Trash, Copy, Check } from "lucide-react";
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setExtractedData } from '../../../store/reducers/index';
import { PaisModal, getPaises } from './PaisModal';
import { ChevronsUpDown } from 'lucide-react';
import { RegimenesModal, getRegimenes } from './RegimenesModal';
import { ProvinciasModal, getProvincias } from './ProvinciasModal';

// Reusable Input with Copy Button Component
const InputWithCopy = ({ id, value, placeholder, onChange, className, type = "text", required = false, maxLength, rows }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (value === null || value === undefined || value === '') return;
    
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Check if value exists (including 0 as a valid value)
  const hasValue = value !== null && value !== undefined && value !== '';

  // Handle value properly - don't convert 0 to empty string
  const inputValue = value === null || value === undefined ? "" : value;

  // For textarea
  if (rows) {
    return (
      <div className="relative">
        <textarea
          id={id}
          rows={rows}
          value={inputValue}
          placeholder={placeholder}
          onChange={onChange}
          className={className}
          required={required}
        />
        {hasValue && (
          <button
            type="button"
            onClick={handleCopy}
            className="absolute right-0 top-auto bottom-2 p-1 hover:bg-gray-100 rounded transition-colors"
            title={copied ? "Copied!" : "Copy"}
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4 text-gray-500" />
            )}
          </button>
        )}
      </div>
    );
  }

  // For input
  return (
    <div className="relative">
      <input
        id={id}
        type={type}
        value={inputValue}
        placeholder={placeholder}
        onChange={onChange}
        className={className}
        required={required}
        maxLength={maxLength}
      />
      {hasValue && (
        <button
          type="button"
          onClick={handleCopy}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
          title={copied ? "Copied!" : "Copy"}
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 text-gray-500" />
          )}
        </button>
      )}
    </div>
  );
};

export const ItemInformation = ({
  declarationIndex,
  itemIndex,
  isSpanish,
  item,
  editableData,
  setEditableData,
  setIsDirty,
}) => {
  const dispatch = useDispatch();
  const [isPaisOrigenModalOpen, setPaisOrigenModalOpen] = useState(false);
  const [isRegimenModalOpen, setRegimenModalOpen] = useState(false);
  const [isProvinciasModalOpen, setProvinciasModalOpen] = useState(false);
  const paises = getPaises();
  const regimenes = getRegimenes();
  const provincias = getProvincias();
  const selectedPaisOrigen = paises.find(
    (p) => p.codigo === item.pais_origen || p.nombre === item.pais_origen
  );
  const selectedRegimen = regimenes.find(
    (r) => r.codigo === item.regimen_solicitado_precedente || r.nombre === item.regimen_solicitado_precedente
  );
  const selectedProvincia = provincias.find(
    (p) => p.codigo === item.provincia_origen || p.nombre === item.provincia_origen || 
           (item.provincia_origen && p.nombre?.toLowerCase() === item.provincia_origen.toLowerCase()) ||
           (item.provincia_origen && p.codigo?.toString() === item.provincia_origen.toString())
  );

  const handleItemChange = (declarationIndex, itemIndex, field, value) => {
    // Deep clone to avoid mutating original data
    const updatedData = JSON.parse(JSON.stringify(editableData));
    if (
      updatedData[declarationIndex].partidas &&
      updatedData[declarationIndex].partidas[itemIndex]
    ) {
      updatedData[declarationIndex].partidas[itemIndex][field] = value;
      setEditableData(updatedData);
      // Don't update extractedData here - only update on save
      setIsDirty(true);
    }
  };

  // Handler for selecting a country from the modal
  const handlePaisOrigenSelect = (pais) => {
    handleItemChange(declarationIndex, itemIndex, 'pais_origen', pais.codigo);
  };

  const handleRegimenSelect = (regimen) => {
    handleItemChange(declarationIndex, itemIndex, 'regimen_solicitado_precedente', regimen.codigo);
  };

  const handleProvinciaSelect = (provincia) => {
    handleItemChange(declarationIndex, itemIndex, 'provincia_origen', provincia.codigo);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold uppercase">
          {!isSpanish ? "Item" : "Partida"} #{itemIndex + 1}
        </h2>
        <button
          className="py-1 text-xs text-red-500 flex items-center gap-1"
          onClick={() => {
            // Deep clone to avoid mutating original data
            const updatedData = JSON.parse(JSON.stringify(editableData));
            updatedData[declarationIndex].partidas.splice(itemIndex, 1);
            setEditableData(updatedData);
            // Don't update extractedData here - only update on save
            setIsDirty(true);
          }}
        >
          <Trash className={`size-4 stroke-red-500`} />
          {!isSpanish ? "Remove Item" : "Eliminar Partida"}
        </button>
      </div>
      <div className="form-group flex-1 ">
        <label
          htmlFor="descripcion_mercancia"
          className={`${!item.descripcion_mercancia && "text-red-600"}`}
        >
          {!isSpanish ? "Goods Description" : "Descripción de Mercancía"}
        </label>
        <InputWithCopy
          id={`descripcion_mercancia_${declarationIndex + 1}_${itemIndex}`}
          rows={3}
          value={item.descripcion_mercancia}
          placeholder={`${
            !item.descripcion_mercancia && !isSpanish
              ? "Enter Goods Description"
              : "Entrada Descripción de Mercancía"
          }`}
          onChange={(e) =>
            handleItemChange(
              declarationIndex,
              itemIndex,
              "descripcion_mercancia",
              e.target.value
            )
          }
          className={`w-full border rounded px-2 py-1 mt-1 pr-8 ${
            !item.descripcion_mercancia && "required-input"
          }`}
          required
        />
      </div>
      <div className="grid grid-cols-3 gap-8 rounded-md mt-4 partida-container">
        <div className="form-group">
          <label
            htmlFor="codigo_nc"
            className={`${!item.codigo_nc && "text-red-600"}`}
          >
            {!isSpanish ? "NC Code" : "Código NC"}
          </label>
          <InputWithCopy
            id={`codigo_nc_${declarationIndex + 1}_${itemIndex}`}
            value={item.codigo_nc}
            placeholder={`${
              !item.codigo_nc && !isSpanish
                ? "Enter NC Code"
                : "Entrada Código NC"
            }`}
            onChange={(e) =>
              handleItemChange(
                declarationIndex,
                itemIndex,
                "codigo_nc",
                e.target.value
              )
            }
            className={`w-full border rounded px-2 py-1 pr-8 ${
              !item.codigo_nc && "required-input"
            }`}
            required
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="masa_bruta"
            className={`${!item.masa_bruta && "text-red-600"}`}
          >
            {!isSpanish ? "Gross Weight" : "Masa Bruta"}
          </label>
          <InputWithCopy
            id={`masa_bruta_${declarationIndex + 1}_${itemIndex}`}
            value={item.masa_bruta}
            placeholder={`${
              !item.masa_bruta && !isSpanish
                ? "Enter Gross Weight"
                : "Entrada Masa Bruta"
            }`}
            onChange={(e) =>
              handleItemChange(
                declarationIndex,
                itemIndex,
                "masa_bruta",
                e.target.value
              )
            }
            className={`w-full border rounded px-2 py-1 pr-8 ${
              !item.masa_bruta && "required-input"
            }`}
            required
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="masa_neta"
            className={`${!item.masa_neta && "text-red-600"}`}
          >
            {!isSpanish ? "Net Weight" : "Masa Neta"}
          </label>
          <InputWithCopy
            id={`masa_neta_${declarationIndex + 1}_${itemIndex}`}
            value={item.masa_neta}
            placeholder={`${
              !item.masa_neta && !isSpanish
                ? "Enter Net Weight"
                : "Entrada Masa Neta"
            }`}
            onChange={(e) =>
              handleItemChange(
                declarationIndex,
                itemIndex,
                "masa_neta",
                e.target.value
              )
            }
            className={`w-full border rounded px-2 py-1 pr-8 ${
              !item.masa_neta && "required-input"
            }`}
            required
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="cantidad_euros"
          >
            {!isSpanish ? "Euro Amount" : "Cantidad en Euros"}
          </label>
          <InputWithCopy
            id={`cantidad_euros_${declarationIndex + 1}_${itemIndex}`}
            value={item.cantidad_euros || ''}
            placeholder={`${
              !item.cantidad_euros && !isSpanish
                ? "Enter Euro Amount"
                : "Entrada Cantidad en Euros"
            }`}
            onChange={(e) =>
              handleItemChange(
                declarationIndex,
                itemIndex,
                "cantidad_euros",
                e.target.value
              )
            }
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="cantidad_divisas"
          >
            {!isSpanish ? "Currency Amount" : "Cantidad en Divisas"}
          </label>
          <InputWithCopy
            id={`cantidad_divisas_${declarationIndex + 1}_${itemIndex}`}
            value={item.cantidad_divisas || ''}
            placeholder={`${
              !item.cantidad_divisas && !isSpanish
                ? "Enter Currency Amount"
                : "Entrada Cantidad en Divisas"
            }`}
            onChange={(e) =>
              handleItemChange(
                declarationIndex,
                itemIndex,
                "cantidad_divisas",
                e.target.value
              )
            }
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="pais_origen"
            className={`${!item.pais_origen && "text-red-600"}`}
          >
            {!isSpanish ? "Country of Origin" : "País de Origen"}
          </label>
          <div className="relative w-full">
            <button
            id={`pais_origen_${declarationIndex + 1}_${itemIndex}`}

              type="button"
              className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${selectedPaisOrigen ? '' : 'opacity-75'}`}
              onClick={() => setPaisOrigenModalOpen(true)}
            >
              {selectedPaisOrigen ? (
                <span className="truncate">{selectedPaisOrigen.codigo} {selectedPaisOrigen.nombre && <span className="text-xs text-gray-400 ml-2">({selectedPaisOrigen.nombre})</span>}</span>
              ) : (
                item.pais_origen ? <span className="truncate">{item.pais_origen}</span> : <span className="truncate">{!isSpanish ? 'Select Country of Origin' : 'Seleccionar País de Origen'}</span>
              )}
              <ChevronsUpDown className="w-4 h-4" />
            </button>
            <PaisModal
              countries={paises}
              isOpen={isPaisOrigenModalOpen}
              onClose={() => setPaisOrigenModalOpen(false)}
              onSelect={handlePaisOrigenSelect}
              selectedCountry={selectedPaisOrigen}
              title={!isSpanish ? 'Select Country of Origin' : 'Seleccionar País de Origen'}
            />
          </div>
        </div>
        <div className="form-group">
          <label
            htmlFor="precio"
          >
            {!isSpanish ? "Price" : "Precio"}
          </label>
          <InputWithCopy
            id={`precio_${declarationIndex + 1}_${itemIndex}`}
            value={item.precio}
            placeholder={`${
              !item.precio && !isSpanish ? "Enter Price" : "Entrada Precio"
            }`}
            onChange={(e) =>
              handleItemChange(
                declarationIndex,
                itemIndex,
                "precio",
                e.target.value
              )
            }
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label
            htmlFor="provincia_origen"
          >
            {!isSpanish ? "Province of Origin" : "Provincia de Origen"}
          </label>
          <div className="relative w-full">
            <button
            id={`provincia_origen_${declarationIndex + 1}_${itemIndex}`}

              type="button"
              className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${selectedProvincia ? '' : 'opacity-75'}`}
              onClick={() => setProvinciasModalOpen(true)}
            >
              {selectedProvincia ? (
                <span className="truncate" style={{ maxWidth: '200px', display: 'inline-block' }} title={`${selectedProvincia.codigo} - ${selectedProvincia.nombre}`}>
                  {selectedProvincia.codigo}
                </span>
              ) : (
                item.provincia_origen ? (
                  (() => {
                    // Try to find provincia by nombre (case-insensitive) to get codigo
                    const foundProvincia = provincias.find(
                      (p) => p.nombre?.toLowerCase() === item.provincia_origen.toLowerCase() ||
                             p.codigo?.toString() === item.provincia_origen.toString()
                    );
                    return foundProvincia ? (
                      <span className="truncate" title={`${foundProvincia.codigo} - ${foundProvincia.nombre}`}>
                        {foundProvincia.codigo}
                      </span>
                    ) : (
                      <span className="truncate">{item.provincia_origen}</span>
                    );
                  })()
                ) : (
                  <span className="truncate">{!isSpanish ? 'Select Province of Origin' : 'Seleccionar Provincia de Origen'}</span>
                )
              )}
              <ChevronsUpDown className="w-4 h-4" />
            </button>
            <ProvinciasModal
              provincias={provincias}
              isOpen={isProvinciasModalOpen}
              onClose={() => setProvinciasModalOpen(false)}
              onSelect={handleProvinciaSelect}
              selectedProvincia={selectedProvincia}
              title={!isSpanish ? 'Select Province of Origin' : 'Seleccionar Provincia de Origen'}
            />
          </div>
        </div>
        <div className="form-group">
          <label
            htmlFor="regimen_solicitado_precedente"
            className={`truncate`}
          >
            {!isSpanish ? "Previous Regime" : "Régimen Solicitado"}
          </label>
          <div className="relative w-full">
            <button
            id={`regimen_solicitado_precedente_${declarationIndex + 1}_${itemIndex}`}

              type="button"
              className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${selectedRegimen ? '' : 'opacity-75'}`}
              onClick={() => setRegimenModalOpen(true)}
            >
              {selectedRegimen ? (
                <span className="truncate" style={{ maxWidth: '200px', display: 'inline-block' }} title={`${selectedRegimen.codigo} - ${selectedRegimen.nombre}`}>
                  {selectedRegimen.codigo} {selectedRegimen.nombre && <span className="text-xs text-gray-400 ml-2">({selectedRegimen.nombre})</span>}
                </span>
              ) : (
                item.regimen_solicitado_precedente ? <span className="truncate">{item.regimen_solicitado_precedente}</span> : <span className="truncate">{!isSpanish ? 'Select Previous Regime' : 'Seleccionar Régimen Solicitado'}</span>
              )}
              <ChevronsUpDown className="w-4 h-4" />
            </button>
            <RegimenesModal
              regimenes={regimenes}
              isOpen={isRegimenModalOpen}
              onClose={() => setRegimenModalOpen(false)}
              onSelect={handleRegimenSelect}
              selectedRegimen={selectedRegimen}
              title={!isSpanish ? 'Select Previous Regime' : 'Seleccionar Régimen Solicitado'}
            />
          </div>
        </div>
        <div className="form-group">
          <label
            htmlFor="valor_estadistico"
          >
            {!isSpanish ? "Statistical Value" : "Valor Estadístico"}
          </label>
          <InputWithCopy
            id={`valor_estadistico_${declarationIndex + 1}_${itemIndex}`}
            value={item.valor_estadistico}
            placeholder={`${
              !item.valor_estadistico && !isSpanish
                ? "Enter Statistical Value"
                : "Entrada Valor Estadístico"
            }`}
            onChange={(e) =>
              handleItemChange(
                declarationIndex,
                itemIndex,
                "valor_estadistico",
                e.target.value
              )
            }
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`adiciones_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "Additions" : "Adiciones"}</label>
          <InputWithCopy
            id={`adiciones_${declarationIndex + 1}_${itemIndex}`}
            value={item.adiciones || ''}
            maxLength={15}
            placeholder={!isSpanish ? "e.g. 0,00" : "Ej. 0,00"}
            onChange={e => handleItemChange(declarationIndex, itemIndex, "adiciones", e.target.value)}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`deducciones_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "Deductions" : "Deducciones"}</label>
          <InputWithCopy
            id={`deducciones_${declarationIndex + 1}_${itemIndex}`}
            value={item.deducciones || ''}
            maxLength={15}
            placeholder={!isSpanish ? "e.g. 0,00" : "Ej. 0,00"}
            onChange={e => handleItemChange(declarationIndex, itemIndex, "deducciones", e.target.value)}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`observaciones_arancelarias_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "Customs Remarks" : "Observaciones Arancelarias"}</label>
          <InputWithCopy
            id={`observaciones_arancelarias_${declarationIndex + 1}_${itemIndex}`}
            value={item.observaciones_arancelarias || ''}
            type="number"
            placeholder={!isSpanish ? "e.g. 512" : "Ej. 512"}
            onChange={e => handleItemChange(declarationIndex, itemIndex, "observaciones_arancelarias", e.target.value)}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`observaciones_arancelarias_2_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "Customs Remarks 2" : "Observaciones Arancelarias 2"}</label>
          <InputWithCopy
            id={`observaciones_arancelarias_2_${declarationIndex + 1}_${itemIndex}`}
            value={item.observaciones_arancelarias_2 || ''}
            type="number"
            placeholder={!isSpanish ? "e.g. 0" : "Ej. 0"}
            onChange={e => handleItemChange(declarationIndex, itemIndex, "observaciones_arancelarias_2", e.target.value)}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        <div className="form-group">
          <label htmlFor={`deducciones_base_iva_${declarationIndex + 1}_${itemIndex}`}>{!isSpanish ? "VAT Deduction Base" : "Deducciones Base IVA"}</label>
          <InputWithCopy
            id={`deducciones_base_iva_${declarationIndex + 1}_${itemIndex}`}
            value={item.deducciones_base_iva || ''}
            maxLength={15}
            placeholder={!isSpanish ? "e.g. 0,00" : "Ej. 0,00"}
            onChange={e => handleItemChange(declarationIndex, itemIndex, "deducciones_base_iva", e.target.value)}
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
        {/* Régimen Comunitario/Nacional - Missing field from PDF */}
        <div className="form-group">
          <label
            htmlFor={`regimen_comunitario_nacional_${declarationIndex + 1}_${itemIndex}`}
            className={`truncate`}
          >
            {!isSpanish ? "Community/National Regime" : "Régimen Comunitario/Nacional"}
          </label>
          <InputWithCopy
            id={`regimen_comunitario_nacional_${declarationIndex + 1}_${itemIndex}`}
            value={item.regimen_comunitario_nacional || ''}
            placeholder={`${
              !item.regimen_comunitario_nacional && !isSpanish
                ? "Enter Community/National Regime"
                : "Entrada Régimen Comunitario/Nacional"
            }`}
            onChange={(e) =>
              handleItemChange(
                declarationIndex,
                itemIndex,
                "regimen_comunitario_nacional",
                e.target.value || null // Explicit null when empty
              )
            }
            className="w-full border rounded px-2 py-1 pr-8"
          />
        </div>
      </div>
      {/* Observaciones per Partida */}
      <div className="form-group flex-1 mt-4">
        <label
          htmlFor={`observaciones_${declarationIndex + 1}_${itemIndex}`}
        >
          {!isSpanish ? "Observations (per Item)" : "Observaciones (por Partida)"}
        </label>
        <InputWithCopy
          id={`observaciones_${declarationIndex + 1}_${itemIndex}`}
          rows={3}
          value={item.observaciones || ''}
          placeholder={`${
            !item.observaciones && !isSpanish
              ? "Enter Observations (e.g. SOIVRE\\nEFITCAN)"
              : "Entrada Observaciones (Ej. SOIVRE\\nEFITCAN)"
          }`}
          onChange={(e) =>
            handleItemChange(
              declarationIndex,
              itemIndex,
              "observaciones",
              e.target.value
            )
          }
          className="w-full border rounded px-2 py-1 mt-1 pr-8"
        />
      </div>
    </div>
  );
};
